import { useEffect, useState } from 'react'
import { theme } from '../theme'
import { useIsAdmin } from '../auth/AuthContext'
import { AdminOnly } from '../components/AdminOnly'

/**
 * Settings -> Quality: the gate thresholds and subscore scales a received fax is judged
 * against (G12, design §8.3).
 *
 * THERE IS NO SCALE-GENERATION CONTROL, AND THAT IS THE POINT. Generation 1 is the arithmetic
 * that shipped, in which three of four subscores saturate at exactly 100.0 on a healthy channel
 * so the composite reads 97.6-or-0.0. Generation 2 is the re-scaled arithmetic, and since the
 * changeover on 2026-09-07 it is the only one this build applies. It used to be a labelled
 * choice here, which was indefensible: scores written under one generation are not comparable
 * with the other and nothing rewrites a stored score, so the only thing the control could do was
 * split an operator's own measurement series across two scales. The generation is now a property
 * of the build, the server reports it read-only, and it is not settable anywhere.
 *
 * Generation 1 is still READABLE — a run scored before the changeover keeps its recorded
 * generation, run detail and the distribution both report it, and the Help glossary explains it
 * as a historical value. Pinning what new scoring produces is not the same as rewriting what was
 * already measured.
 *
 * THE BOUNDS AND THE AVAILABLE LADDER STEPS COME FROM THE SERVER, not from constants
 * duplicated here, so this form cannot advertise a limit the validation does not enforce.
 */

interface QualityConfig {
  /** The generation in force, reported by the server. Read-only — there is no input for it. */
  scale_version: number
  resolution_modulation_floor: number
  resolution_modulation_full: number
  geometry_skew_deadband_degrees: number
  geometry_skew_zero_degrees: number
  noise_speckle_zero_fraction: number
  noise_dropout_zero_fraction: number
  noise_gap_zero_px: number
  legibility_lp_per_inch: number
  legibility_min_modulation: number
  integrity_max_dropout_fraction: number
  integrity_max_gap_px: number
  cleanliness_max_speckle_fraction: number
  fidelity_min_worst_window_ssim: number
  fidelity_max_mean_tone_error: number
  job_overridable_fields: string[]
  available_lp_per_inch: number[]
}

type NumericField = Exclude<
  keyof QualityConfig,
  'scale_version' | 'job_overridable_fields' | 'available_lp_per_inch'
>

const card: React.CSSProperties = {
  background: theme.bgAlt,
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
  padding: '1.2rem',
  marginBottom: '1.5rem',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.45rem 0.6rem',
  background: theme.bg,
  color: theme.text,
  border: `1px solid ${theme.border}`,
  borderRadius: 4,
  fontSize: '0.9rem',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8rem',
  color: theme.textMuted,
  marginBottom: '0.2rem',
}

const noteStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  color: theme.textMuted,
  marginTop: '0.25rem',
}

/** One numeric input plus the reason its value is what it is. */
function Field({
  name,
  label,
  note,
  value,
  step,
  onChange,
}: {
  name: NumericField
  label: string
  note: string
  value: number | undefined
  step: number
  onChange: (name: NumericField, value: number) => void
}) {
  return (
    <div style={{ marginBottom: '0.9rem' }}>
      <label style={labelStyle} htmlFor={name}>
        {label}
      </label>
      <input
        id={name}
        style={inputStyle}
        type="number"
        step={step}
        value={value ?? ''}
        onChange={(e) => onChange(name, Number(e.target.value))}
      />
      <div style={noteStyle}>{note}</div>
    </div>
  )
}

export default function QualitySettings() {
  const isAdmin = useIsAdmin()
  const [config, setConfig] = useState<QualityConfig | null>(null)
  const [form, setForm] = useState<Partial<QualityConfig>>({})
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const loadConfig = async () => {
    try {
      const res = await fetch('/api/settings/quality-config', { credentials: 'same-origin' })
      if (!res.ok) {
        setError(res.status === 403 ? null : 'Could not read quality gate settings')
        return
      }
      const body: QualityConfig = await res.json()
      setConfig(body)
      setForm(body)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadConfig()
  }, [])

  const setField = (name: NumericField, value: number) =>
    setForm((prev) => ({ ...prev, [name]: value }))

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSaved(false)
    setSaving(true)
    try {
      const res = await fetch('/api/settings/quality-config', {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resolution_modulation_floor: form.resolution_modulation_floor,
          resolution_modulation_full: form.resolution_modulation_full,
          geometry_skew_deadband_degrees: form.geometry_skew_deadband_degrees,
          geometry_skew_zero_degrees: form.geometry_skew_zero_degrees,
          noise_speckle_zero_fraction: form.noise_speckle_zero_fraction,
          noise_dropout_zero_fraction: form.noise_dropout_zero_fraction,
          noise_gap_zero_px: form.noise_gap_zero_px,
          legibility_lp_per_inch: form.legibility_lp_per_inch,
          legibility_min_modulation: form.legibility_min_modulation,
          integrity_max_dropout_fraction: form.integrity_max_dropout_fraction,
          integrity_max_gap_px: form.integrity_max_gap_px,
          cleanliness_max_speckle_fraction: form.cleanliness_max_speckle_fraction,
          fidelity_min_worst_window_ssim: form.fidelity_min_worst_window_ssim,
          fidelity_max_mean_tone_error: form.fidelity_max_mean_tone_error,
        }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => null)
        const detail = body?.detail
        setError(
          typeof detail === 'string'
            ? detail
            : Array.isArray(detail)
              ? detail.map((d: any) => d?.msg ?? String(d)).join('; ')
              : 'Could not save quality gate settings',
        )
        return
      }
      const body: QualityConfig = await res.json()
      setConfig(body)
      setForm(body)
      setSaved(true)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div>Loading…</div>
  if (!isAdmin || !config) {
    return (
      <div>
        <h2 style={{ marginTop: 0 }}>Quality</h2>
        <AdminOnly what="Quality gate settings" />
      </div>
    )
  }

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Quality gates</h2>
      <p style={{ color: theme.textMuted, fontSize: '0.85rem', maxWidth: 760 }}>
        Four gates are evaluated independently on every scored fax — <strong>legibility</strong>,{' '}
        <strong>integrity</strong>, <strong>cleanliness</strong> and <strong>fidelity</strong> —
        and each names itself and its measured value when it fails. The composite score is
        retained as a trend number and is no longer the primary answer: “failed dropout at 4.1%”
        is actionable, “scored 71” is not.
      </p>
      <p style={{ color: theme.textMuted, fontSize: '0.85rem', maxWidth: 760 }}>
        If fewer than three of the four calibration marks are found on the received page, the fax
        is reported as <code>registration_failed</code>: neither a pass nor a failure, because
        nothing could be measured. The gates need those marks as their frame of reference, so
        without them there is nothing to judge the page against.
      </p>

      <form onSubmit={save}>
        <div style={card}>
          <h3 style={{ marginTop: 0, fontSize: '1rem' }}>Gate thresholds</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1.2rem' }}>
            <Field
              name="legibility_lp_per_inch"
              label="legibility: ladder step (lp/in)"
              note={`The step that matters for reading text. The target carries ${config.available_lp_per_inch.join(', ')} lp/in; a value not on the ladder has nothing to measure.`}
              value={form.legibility_lp_per_inch}
              step={1}
              onChange={setField}
            />
            <Field
              name="legibility_min_modulation"
              label="legibility: minimum modulation"
              note="Michelson contrast the step must reach, on the WORSE axis — fax resolution is anisotropic and text is illegible if it smears either way."
              value={form.legibility_min_modulation}
              step={0.01}
              onChange={setField}
            />
            <Field
              name="integrity_max_dropout_fraction"
              label="integrity: maximum dropout"
              note="Fraction of the solid-black field that came back white."
              value={form.integrity_max_dropout_fraction}
              step={0.005}
              onChange={setField}
            />
            <Field
              name="integrity_max_gap_px"
              label="integrity: maximum break (px)"
              note="Longest run of non-ink along a thin rule."
              value={form.integrity_max_gap_px}
              step={1}
              onChange={setField}
            />
            <Field
              name="cleanliness_max_speckle_fraction"
              label="cleanliness: maximum speckle"
              note="Fraction of the solid-white field that came back black, measured inside the patch's own 0.5pt outline."
              value={form.cleanliness_max_speckle_fraction}
              step={0.005}
              onChange={setField}
            />
            <Field
              name="fidelity_min_worst_window_ssim"
              label="fidelity: minimum worst-window SSIM"
              note="Worst window, not the page mean: a crease or a band of dropout is local, and a mean averages it away — which is the only reason whole-page SSIM is worth having alongside the target patches."
              value={form.fidelity_min_worst_window_ssim}
              step={0.01}
              onChange={setField}
            />
            <Field
              name="fidelity_max_mean_tone_error"
              label="fidelity: maximum dot-coverage error"
              note="Mean absolute error across the coverage patches. On a bilevel channel this measures dither fidelity, not greyscale response."
              value={form.fidelity_max_mean_tone_error}
              step={0.01}
              onChange={setField}
            />
          </div>
        </div>

        <div style={card}>
          <h3 style={{ marginTop: 0, fontSize: '1rem' }}>Subscore scales</h3>
          <div style={noteStyle}>
            These shape the four 0–100 subscores. Every fax this build scores is scored on
            generation {config.scale_version} of the arithmetic, which is fixed for the whole
            deployment and is not a setting — two runs on two different scales would not be
            comparable, and comparing them is the entire purpose of the score. Each run records
            the generation and the thresholds it was judged against.
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '0 1.2rem',
              marginTop: '0.8rem',
            }}
          >
            <Field
              name="resolution_modulation_floor"
              label="resolution: modulation floor"
              note="Contrast at or below which a grating earns no credit — indistinguishable from a flat field."
              value={form.resolution_modulation_floor}
              step={0.01}
              onChange={setField}
            />
            <Field
              name="resolution_modulation_full"
              label="resolution: full-credit modulation"
              note="Contrast at which a step earns full credit. Demanding on purpose: full marks then require the 100 lp/in step to modulate, which a 204 dpi channel never fully achieves — so the top of the scale stays unreached and the number keeps headroom."
              value={form.resolution_modulation_full}
              step={0.01}
              onChange={setField}
            />
            <Field
              name="geometry_skew_deadband_degrees"
              label="geometry: skew deadband (degrees)"
              note="Skew below this is not penalised. A one-pixel fiducial centroid error over the 480pt baseline reads as 0.042 degrees, so anything under ~0.04 would penalise pages for rasterisation rather than for skew."
              value={form.geometry_skew_deadband_degrees}
              step={0.01}
              onChange={setField}
            />
            <Field
              name="geometry_skew_zero_degrees"
              label="geometry: skew scoring zero (degrees)"
              note="Skew at which the penalty is total. Past ~3 degrees a page is a feed fault rather than a quality gradient, and a wider range compresses everything real into its first tenth."
              value={form.geometry_skew_zero_degrees}
              step={0.1}
              onChange={setField}
            />
            <Field
              name="noise_speckle_zero_fraction"
              label="noise: speckle scoring zero"
              note="Speckle fraction at which the speckle penalty is total."
              value={form.noise_speckle_zero_fraction}
              step={0.005}
              onChange={setField}
            />
            <Field
              name="noise_dropout_zero_fraction"
              label="noise: dropout scoring zero"
              note="Dropout fraction at which the dropout penalty is total."
              value={form.noise_dropout_zero_fraction}
              step={0.005}
              onChange={setField}
            />
            <Field
              name="noise_gap_zero_px"
              label="noise: rule break scoring zero (px)"
              note="Break length at which the continuity penalty is total. A reading of zero is scored as one pixel, because the largest true value consistent with “nothing detected” is the measurement's own resolution — which is why a clean page now scores high rather than exactly 100."
              value={form.noise_gap_zero_px}
              step={1}
              onChange={setField}
            />
          </div>
        </div>

        <div style={card}>
          <h3 style={{ marginTop: 0, fontSize: '1rem' }}>Per-job overrides</h3>
          <div style={noteStyle}>
            A job may override any of these thresholds through its{' '}
            <code>quality_thresholds</code> field: {config.job_overridable_fields.join(', ')}. An
            absent key inherits, so a later change here still reaches a job that overrode only one
            value. The scoring generation is not among them and is not settable at all — it is
            fixed for the deployment.
          </div>
        </div>

        {error ? (
          <div style={{ color: '#ef9a9a', marginBottom: '0.8rem', fontSize: '0.85rem' }}>
            {error}
          </div>
        ) : null}
        {saved ? (
          <div style={{ color: '#a5d6a7', marginBottom: '0.8rem', fontSize: '0.85rem' }}>
            Saved. Every worker picks the new values up within ten seconds.
          </div>
        ) : null}
        <button type="submit" disabled={saving}>
          {saving ? 'Saving…' : 'Save quality gate settings'}
        </button>
      </form>
    </div>
  )
}
